<template>
  <div>
    <ul ref="container" class="relative space-y-2">
      <li
        v-for="(item, index) in processedData"
        :key="item.name"
        class="text-sm"
        :class="[alignLeft ? 'flex items-end' : 'w-1/2 mx-auto']"
      >
        <!-- question label -->
        <div v-if="alignLeft" class="w-1/3 text-right pr-4">
          <span class="bg-white">{{ item.name }}</span>
        </div>
        <div
          v-else
          :style="{
            transform: `translate(${((width / 2) * item.translation) / 100}px)`,
            width: `${width -
              width * 0.25 -
              ((width / 2) * item.translation) / 100}px`,
          }"
        >
          <span class="bg-white">{{ item.name }}</span>
        </div>
        <!-- chart container -->
        <div ref="chartContainer" :class="[alignLeft ? 'w-2/3' : '']">
          <!-- average markers -->
          <svg
            ref="svg"
            :width="chartWidth"
            :height="svgHeight"
            class="overflow-visible text-gray-800"
          >
            <g
              id="pacientes"
              :data="item.avgP"
              :class="[
                highlight === 'pacientes' ? 'opacity-100' : 'opacity-25',
              ]"
              class="cursor-pointer"
              @mouseenter="showTooltipMarker($event, 'pacientes', item.avgP)"
              @mouseleave="hideTooltip"
              @click="changeSegment('pacientes')"
            >
              <text
                v-if="index"
                text-anchor="middle"
                :x="avgScale(item.avgP)"
                y="16"
              >
                P
              </text>
              <text v-else text-anchor="end" :x="avgScale(item.avgP)" y="16">
                Pacientes
              </text>
              <polygon
                :points="
                  `${avgScale(item.avgP) - 8},${svgHeight - 10} 
              ${avgScale(item.avgP)},${svgHeight - 3} 
              ${avgScale(item.avgP) + 8},${svgHeight - 10}`
                "
                fill="#6B7280"
              ></polygon>
            </g>
            <g
              id="familiares"
              :data="item.avgF"
              :class="[
                highlight === 'familiares' ? 'opacity-100' : 'opacity-25',
              ]"
              class="cursor-pointer"
              @mouseenter="showTooltipMarker($event, 'familiares', item.avgF)"
              @mouseleave="hideTooltip"
              @click="changeSegment('familiares')"
            >
              <text
                v-if="index"
                text-anchor="middle"
                :x="avgScale(item.avgF)"
                y="16"
              >
                F
              </text>
              <text v-else text-anchor="start" :x="avgScale(item.avgF)" y="16">
                Familiares
              </text>
              <polygon
                :points="
                  `${avgScale(item.avgF) - 8},${svgHeight - 10} 
              ${avgScale(item.avgF)},${svgHeight - 3} 
              ${avgScale(item.avgF) + 8},${svgHeight - 10}`
                "
                fill="#6B7280"
              ></polygon>
            </g>
          </svg>
          <!-- bar container -->
          <div
            class="flex h-7 rounded overflow-hidden transition-all duration-500 transform"
            :class="{ 'space-x-2': !stacked, 'w-1/2 mx-auto': alignLeft }"
            :style="{ transform: `translate(${item.translation}%)` }"
          >
            <!-- segments -->
            <div
              v-for="(value, i) in item.values"
              :key="`${item.name}-${value.name}`"
              class="text-white h-full transition-all duration-500 transform"
              :style="{
                'will-change': 'transform, width',
                width: stacked
                  ? `${value.value * max[i]}%`
                  : `${100 / options}%`,
                transform: !stacked
                  ? i === 3
                    ? `translateX(calc(-100% - 0.5rem))`
                    : i === 2
                    ? `translateX(calc(100% + 0.5rem))`
                    : null
                  : null,
              }"
            >
              <div
                class="flex items-center justify-center h-full transition-all duration-500 bg-opacity-90"
                :class="{
                  [colorClasses[value.name]]: true,
                  rounded: !stacked,
                }"
                :style="{
                  'will-change': 'width',
                  width: stacked ? '100%' : `${(value.value * 100) / max[i]}%`,
                  background: colors[i],
                }"
                @mouseenter="showTooltipSegment($event, value)"
                @mouseleave="hideTooltip"
              >
                <!-- value label -->
                <!-- <span
                  v-show="
                    !stacked
                      ? (value.value * 100) / max[i] > 8
                      : value.value > 4
                  "
                  class="tabular-nums"
                >
                  <TweeningNumber :num="value.value" :decimals="0" />%</span
                > -->
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
    <VTooltip :show="tooltip.show" :target="tooltip.target" class="z-30">
      <div
        class="bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg text-xs flex items-center"
      >
        <div class="">
          <div v-html="tooltip.html"></div>
        </div>
      </div>
    </VTooltip>
  </div>
</template>

<script>
import * as d3 from "d3";
import VTooltip from "@/components/VTooltip";

// import TweeningNumber from "@/components/TweeningNumber";
export default {
  name: "DivergingStackedBars",
  components: { VTooltip },
  props: {
    data: {
      type: Array,
      default: () => [
        {
          name: "Data 1",
          values: [
            { name: "val 1", value: 60 },
            { name: "val 2", value: 40 },
          ],
        },
        {
          name: "Data 2",
          values: [
            { name: "val 1", value: 30 },
            { name: "val 2", value: 70 },
          ],
        },
      ],
    },
    selected: {
      type: String,
      default: null,
    },
    activeClass: {
      type: String,
      default: "",
    },
    barClass: {
      type: String,
      default: "bg-gray-300",
    },
    compareClass: {
      type: String,
      default: "bg-gray-600 bg-opacity-50",
    },
    colorClasses: {
      type: Object,
      default: () => ({ "val 1": "bg-blue-500", "val 2": "bg-yellow-500" }),
    },
    colors: {
      type: Object,
      default: () => ({
        0: "#fc7d0a",
        1: "#5fa2ce",
      }),
    },
    stacked: {
      type: Number,
      default: 1,
    },
    alignLeft: {
      type: Boolean,
      default: true,
    },
    scoreDomain: {
      type: Array,
      default: () => [0, 4],
    },
  },
  data() {
    return {
      width: 600,
      chartWidth: 300,
      svgHeight: 30,
      tooltip: {
        show: false,
        target: null,
        segment: "",
        value: 0,
        html: "",
      },
      hover: null,
    };
  },
  computed: {
    max() {
      // return max(
      //   this.data.map((e) => e.values.reduce((a, b) => a + b.value, 0))
      // );
      // if (this.stacked) return this.data[0].values.map(() => 1);
      // const maximums = [];
      // this.data.forEach((e) => {
      //   e.values.forEach((e, i) => {
      //     if (!maximums[i]) maximums[i] = [];
      //     maximums[i].push(e.value);
      //   });
      // });
      // return maximums.map((e) => d3.max(e) || 1);
      return this.data[0].values.map(() => 1);
    },
    options() {
      return this.data[0].values.length;
    },
    processedData() {
      return this.data.map((d) => {
        let left = 0; // store values left of neutral
        let num = this.options / 2; // num of options on each side

        // calculate total values on left side
        for (let i = 0; i < Math.floor(num); i++) {
          left += d.values[i].value;
        }
        // if options is odd, add another half
        if (this.options % 2) left += d.values[Math.floor(num)].value / 2;

        // calculate offset from middle in percentage
        let translation = left < 50 ? 50 - left : (left - 50) * -1;

        return { ...d, translation };
      });
    },
    avgScale() {
      return d3
        .scaleLinear()
        .domain(this.scoreDomain)
        .range([0, this.chartWidth]);
    },
    highlight() {
      return this.hover || this.data[0].people.toLowerCase();
    },
  },
  mounted() {
    this.setSize();

    if (ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.setSize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.setSize);
    }
  },
  beforeDestroy() {
    this.removeResizeObserver();
  },
  methods: {
    setSize() {
      const container = this.$refs.container;
      this.width = container.clientWidth;

      const chartContainer = this.$refs.chartContainer[0];
      this.chartWidth = chartContainer.clientWidth;
    },
    removeResizeObserver() {
      if (ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs.container);
      } else {
        // for IE
        window.removeEventListener("resize", this.setSize);
      }
    },
    changeSegment(segment) {
      this.$emit("click", segment);
    },
    showTooltipMarker({ target }, segment, value) {
      this.tooltip.target = target;
      this.tooltip.show = true;
      this.tooltip.html = `Los
            <span class="font-bold capitalize">${segment}</span>
            tienen una media de
            <span class="font-bold">${value.toFixed(1)}</span>`;

      this.hover = segment;
    },
    showTooltipSegment({ target }, value) {
      this.tooltip.target = target;
      this.tooltip.show = true;
      this.tooltip.html = `<div class="font-bold">${value.name}</div>
          <div>${value.value}%</div>`;
    },
    hideTooltip() {
      this.tooltip.show = false;
      this.hover = null;
    },
  },
};
</script>
<style scoped>
svg {
  overflow: visible !important;
}
</style>
